<template>
  <div class="staff-bulk-upload">
    <PageSpinner :onloader="processing || onloader === 'flex' ? 'flex' : 'none'" />

    <!-- 1. Header Information & File Upload Area -->
    <div class="card mb-3 border-0 shadow-sm bg-secondary">
      <div class="card-body py-3">
        <div class="row align-items-center">
          <div class="col-md-7 mb-3 mb-md-0">
            <h5 class="mb-1 text-primary">
              <i class="fa fa-file-excel mr-2"></i>Upload Staff Excel File (.xlsx, .xls, .csv)
            </h5>
            <small class="text-muted d-block mb-2">
              Import staff details and account passwords. Review each record in the editable table before background submission.
            </small>

            <base-file-upload
              ref="staffUploadComp"
              class="mb-2"
              accept=".xlsx, .xls, .csv"
              icon="fa fa-file-excel"
              title="Choose Staff Excel/CSV spreadsheet or drag & drop here"
              subtitle="Auto-extracts staff details, categories, roles, and contacts into table"
              button-text="Browse File"
              :disabled="processing"
              :selected-name="selectedFileName"
              @change="handleFileUpload"
              @clear="resetBulkUpload"
            />
          </div>

          <div class="col-md-5 text-md-right">
            <button
              type="button"
              class="btn btn-outline-info btn-sm mr-2 mb-2"
              @click="downloadTemplate"
              :disabled="processing"
              title="Download sample Excel template"
            >
              <i class="fa fa-download mr-1"></i>Sample Template
            </button>
            <button
              type="button"
              class="btn btn-outline-primary btn-sm mr-2 mb-2"
              @click="addNewRow"
              :disabled="processing"
              title="Add a new blank staff row"
            >
              <i class="fa fa-plus mr-1"></i>Add Row
            </button>
            <button
              type="button"
              class="btn btn-outline-danger btn-sm mb-2"
              @click="resetBulkUpload"
              :disabled="processing || rows.length === 0"
              title="Clear all extracted records"
            >
              <i class="fa fa-trash mr-1"></i>Reset List
            </button>
          </div>
        </div>

        <div v-if="parseError" class="alert alert-danger mb-0 mt-2 py-2">
          <i class="fa fa-exclamation-triangle mr-2"></i>{{ parseError }}
        </div>
      </div>
    </div>

    <!-- 2. Processing & Progress Tracking Card -->
    <div v-if="processing || hasRunOnce" class="card mb-3 border-0 shadow-sm">
      <div class="card-body py-3">
        <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap">
          <h5 class="mb-0">
            <i class="fa fa-tasks mr-2 text-primary"></i>Background Sequential Processing
          </h5>
          <span class="badge" :class="processing ? 'badge-primary' : (failedCount > 0 ? 'badge-warning' : 'badge-success')">
            {{ processing ? 'Processing In Progress...' : (failedCount > 0 ? 'Processing Completed with Errors' : 'Processing Completed Successfully') }}
          </span>
        </div>

        <!-- Progress bar -->
        <div class="progress my-2" style="height: 16px;">
          <div
            class="progress-bar progress-bar-striped"
            :class="processing ? 'progress-bar-animated bg-primary' : (failedCount > 0 ? 'bg-warning' : 'bg-success')"
            role="progressbar"
            :style="{ width: progressPercentage + '%' }"
            :aria-valuenow="progressPercentage"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            {{ progressPercentage }}%
          </div>
        </div>

        <!-- Metric Badges -->
        <div class="d-flex flex-wrap mt-2 font-weight-bold">
          <div class="badge badge-light border p-2 mr-2 mb-2">
            Total Staff: <span class="text-dark">{{ rows.length }}</span>
          </div>
          <div class="badge badge-info p-2 mr-2 mb-2">
            Processed: <span>{{ processedCount }} / {{ rows.length }}</span>
          </div>
          <div class="badge badge-success p-2 mr-2 mb-2">
            <i class="fa fa-check mr-1"></i>Success: <span>{{ successCount }}</span>
          </div>
          <div class="badge badge-danger p-2 mr-2 mb-2">
            <i class="fa fa-times mr-1"></i>Failed: <span>{{ failedCount }}</span>
          </div>
          <div class="badge badge-warning p-2 mr-2 mb-2" v-if="duplicateWarningCount > 0">
            <i class="fa fa-copy mr-1"></i>Duplicate Warnings: <span>{{ duplicateWarningCount }}</span>
          </div>
        </div>

        <div v-if="processing" class="text-muted small mt-1">
          <i class="fa fa-spinner fa-spin mr-1"></i> Submitting staff {{ currentIndex + 1 }} of {{ rows.length }}...
        </div>
      </div>
    </div>

    <!-- 3. Editable Data Table Section -->
    <div v-if="rows.length > 0" class="card border-0 shadow-sm mb-3">
      <div class="card-header bg-transparent d-flex justify-content-between align-items-center py-2 flex-wrap">
        <div class="mb-2 mb-md-0">
          <h5 class="mb-0 text-primary d-flex align-items-center flex-wrap">
            <i class="fa fa-users mr-2"></i>
            <span>Editable Staff Records</span>
            <span class="badge badge-primary ml-2">{{ rows.length }}</span>
          </h5>
          <small class="text-muted">
            Review and correct any field before starting sequential background submission.
          </small>
        </div>

        <div class="my-1 d-flex flex-wrap align-items-center">
          <!-- View switcher pills -->
          <div class="btn-group btn-group-sm mr-2 mb-1" role="group" aria-label="View switch">
            <button
              type="button"
              class="btn"
              :class="effectiveViewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'"
              @click="viewMode = 'table'"
              title="Table view"
            >
              <i class="fa fa-table mr-1"></i>Table
            </button>
            <button
              type="button"
              class="btn"
              :class="effectiveViewMode === 'card' ? 'btn-primary' : 'btn-outline-primary'"
              @click="viewMode = 'card'"
              title="Card view (optimized for mobile)"
            >
              <i class="fa fa-th-large mr-1"></i>Cards
            </button>
          </div>

          <button
            type="button"
            class="btn btn-sm btn-outline-info mr-2 mb-1"
            @click="autofillMissingServiceNos"
            :disabled="processing || rows.length === 0"
            title="Auto-generate sequential service numbers for empty rows"
          >
            <i class="fa fa-magic mr-1"></i><span class="d-none d-sm-inline">Auto-Fill </span>Service Nos
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-warning mr-2 mb-1"
            @click="filterErrorsOnly = !filterErrorsOnly"
            v-if="hasValidationErrors || failedCount > 0"
          >
            <i class="fa fa-filter mr-1"></i>{{ filterErrorsOnly ? 'All' : 'Issues Only' }}
          </button>
          <button
            type="button"
            class="btn btn-sm btn-warning mr-2 mb-1"
            @click="retryFailed"
            :disabled="processing || failedCount === 0"
            v-if="failedCount > 0"
          >
            <i class="fa fa-redo mr-1"></i>Retry ({{ failedCount }})
          </button>
          <button
            type="button"
            class="btn btn-sm btn-success px-3 mb-1"
            @click="startBulkSubmission"
            :disabled="processing || pendingOrFailedCount === 0 || hasValidationErrors"
          >
            <i class="fa fa-cloud-upload-alt mr-1"></i>
            {{ processing ? 'Submitting...' : `Submit (${pendingOrFailedCount})` }}
          </button>
        </div>
      </div>

      <!-- DESKTOP / TABLE VIEW -->
      <div v-if="effectiveViewMode === 'table'" class="table-responsive bulk-table-wrapper" style="max-height: 520px; overflow-y: auto;">
        <table class="table table-sm table-hover align-items-center mb-0 bulk-table">
          <thead class="thead-light sticky-top">
            <tr>
              <th style="width: 45px;">#</th>
              <th style="width: 105px;">Status</th>
              <th style="min-width: 140px;">Service No *</th>
              <th style="min-width: 90px;">Title *</th>
              <th style="min-width: 140px;">Surname *</th>
              <th style="min-width: 140px;">First Name *</th>
              <th style="min-width: 130px;">Other Name</th>
              <th style="min-width: 170px;">Email *</th>
              <th style="min-width: 150px;">Password *</th>
              <th style="min-width: 130px;">Phone</th>
              <th style="min-width: 110px;">Sex *</th>
              <th style="min-width: 140px;">Staff Type *</th>
              <th style="min-width: 130px;">Marital Status</th>
              <th style="min-width: 135px;">DOB</th>
              <th style="min-width: 190px;">Full Address *</th>
              <th style="width: 55px;" class="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in visibleRows"
              :key="row._rowId"
              :class="{
                'table-danger-light': row._errors && row._errors.length > 0,
                'table-success-light': row.status === 'success',
                'table-info-light': row.status === 'processing'
              }"
            >
              <td class="font-weight-bold text-muted text-center align-middle">
                {{ row._originalIndex + 1 }}
              </td>

              <!-- Status -->
              <td class="align-middle">
                <span v-if="row.status === 'success'" class="badge badge-success">
                  <i class="fa fa-check mr-1"></i>Uploaded
                </span>
                <span v-else-if="row.status === 'failed'" class="badge badge-danger" :title="row.errorMsg">
                  <i class="fa fa-times mr-1"></i>Failed
                </span>
                <span v-else-if="row.status === 'processing'" class="badge badge-primary">
                  <i class="fa fa-spinner fa-spin mr-1"></i>Saving
                </span>
                <span v-else-if="row._errors && row._errors.length > 0" class="badge badge-warning" :title="row._errors.join('; ')">
                  <i class="fa fa-exclamation-triangle mr-1"></i>Invalid
                </span>
                <span v-else class="badge badge-light border">
                  Ready
                </span>
                <div v-if="row.errorMsg" class="text-danger small mt-1" style="max-width: 120px; font-size: 11px; line-height: 1.2;">
                  {{ row.errorMsg }}
                </div>
              </td>

              <!-- Service No -->
              <td class="align-middle">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'ServiceNo')}"
                  v-model.trim="row.ServiceNo"
                  :disabled="processing || row.status === 'success'"
                  placeholder="Service No *"
                  @input="validateAllBulkRows"
                />
              </td>

              <!-- Title -->
              <td class="align-middle">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'Title')}"
                  v-model.trim="row.Title"
                  :disabled="processing || row.status === 'success'"
                  placeholder="Mr / Mrs *"
                  @input="validateAllBulkRows"
                />
              </td>

              <!-- Surname -->
              <td class="align-middle">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'LastName')}"
                  v-model.trim="row.LastName"
                  :disabled="processing || row.status === 'success'"
                  placeholder="Surname *"
                  @input="validateAllBulkRows"
                />
              </td>

              <!-- First Name -->
              <td class="align-middle">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'FirstName')}"
                  v-model.trim="row.FirstName"
                  :disabled="processing || row.status === 'success'"
                  placeholder="First Name *"
                  @input="validateAllBulkRows"
                />
              </td>

              <!-- Other Name -->
              <td class="align-middle">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model.trim="row.MiddleName"
                  :disabled="processing || row.status === 'success'"
                  placeholder="Other Name"
                />
              </td>

              <!-- Email Address -->
              <td class="align-middle">
                <input
                  type="email"
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'EmailAddress')}"
                  v-model.trim="row.EmailAddress"
                  :disabled="processing || row.status === 'success'"
                  placeholder="Email Address *"
                  @input="validateAllBulkRows"
                />
              </td>

              <!-- Phone Number -->
              <td class="align-middle">
                <input
                  type="password"
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'UserPassword')}"
                  v-model.trim="row.UserPassword"
                  :disabled="processing || row.status === 'success'"
                  placeholder="Password *"
                  autocomplete="new-password"
                  @input="validateAllBulkRows"
                />
              </td>

              <!-- Phone Number -->
              <td class="align-middle">
                <input
                  type="tel"
                  class="form-control form-control-sm"
                  v-model.trim="row.PhoneNumber"
                  :disabled="processing || row.status === 'success'"
                  placeholder="Phone"
                />
              </td>

              <!-- Sex -->
              <td class="align-middle">
                <select
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'SexId')}"
                  v-model="row.SexId"
                  :disabled="processing || row.status === 'success'"
                  @change="validateAllBulkRows"
                >
                  <option value="0">Select...</option>
                  <option value="10">Male</option>
                  <option value="11">Female</option>
                </select>
              </td>

              <!-- Staff Type / Category -->
              <td class="align-middle">
                <select
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'CategoryId')}"
                  v-model="row.CategoryId"
                  :disabled="processing || row.status === 'success'"
                  @change="validateAllBulkRows"
                >
                  <option value="0">Select...</option>
                  <option value="14">Teaching</option>
                  <option value="15">Non-Teaching</option>
                </select>
              </td>

              <!-- Marital Status -->
              <td class="align-middle">
                <select
                  class="form-control form-control-sm"
                  v-model="row.MaritalStatusId"
                  :disabled="processing || row.status === 'success'"
                >
                  <option value="0">Unspecified</option>
                  <option value="12">Married</option>
                  <option value="13">Single</option>
                </select>
              </td>

              <!-- Date of Birth -->
              <td class="align-middle">
                <input
                  type="date"
                  class="form-control form-control-sm"
                  v-model="row.DOB"
                  :disabled="processing || row.status === 'success'"
                  :max="todayDate"
                />
              </td>

              <!-- Full Address -->
              <td class="align-middle">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'FullAddress')}"
                  v-model.trim="row.FullAddress"
                  :disabled="processing || row.status === 'success'"
                  placeholder="Full Address *"
                  @input="validateAllBulkRows"
                />
              </td>

              <!-- Remove Action -->
              <td class="align-middle text-center">
                <button
                  type="button"
                  class="btn btn-outline-danger btn-sm p-1"
                  style="width: 28px; height: 28px; line-height: 1;"
                  @click="removeRow(row._originalIndex)"
                  :disabled="processing || row.status === 'processing'"
                  title="Remove row"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- MOBILE / CARD VIEW -->
      <div v-else class="mobile-cards-container p-2" style="max-height: 540px; overflow-y: auto;">
        <div
          v-for="row in visibleRows"
          :key="row._rowId"
          class="card mb-3 border shadow-sm mobile-record-card"
          :class="{
            'border-danger': row._errors && row._errors.length > 0,
            'border-success': row.status === 'success',
            'border-primary': row.status === 'processing'
          }"
        >
          <!-- Card Header: Index, Status & Delete button -->
          <div class="card-header py-2 px-3 bg-light d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <span class="badge badge-dark mr-2">#{{ row._originalIndex + 1 }}</span>
              <span v-if="row.status === 'success'" class="badge badge-success">
                <i class="fa fa-check mr-1"></i>Uploaded
              </span>
              <span v-else-if="row.status === 'failed'" class="badge badge-danger">
                <i class="fa fa-times mr-1"></i>Failed
              </span>
              <span v-else-if="row.status === 'processing'" class="badge badge-primary">
                <i class="fa fa-spinner fa-spin mr-1"></i>Saving
              </span>
              <span v-else-if="row._errors && row._errors.length > 0" class="badge badge-warning">
                <i class="fa fa-exclamation-triangle mr-1"></i>{{ row._errors.length }} Issue(s)
              </span>
              <span v-else class="badge badge-light border">
                Ready
              </span>
            </div>

            <button
              type="button"
              class="btn btn-outline-danger btn-sm py-1 px-2"
              @click="removeRow(row._originalIndex)"
              :disabled="processing || row.status === 'processing'"
              title="Remove row"
            >
              <i class="fa fa-trash mr-1"></i>Delete
            </button>
          </div>

          <!-- Card Body: Form Fields Stacked in responsive grid -->
          <div class="card-body p-3">
            <div v-if="row.errorMsg" class="alert alert-danger py-1 px-2 mb-2 small">
              <i class="fa fa-exclamation-circle mr-1"></i>{{ row.errorMsg }}
            </div>
            <div v-if="row._errors && row._errors.length > 0" class="alert alert-warning py-1 px-2 mb-2 small">
              <ul class="mb-0 pl-3">
                <li v-for="(err, eIdx) in row._errors" :key="eIdx">{{ err }}</li>
              </ul>
            </div>

            <div class="row">
              <!-- Service No -->
              <div class="col-6 col-sm-4 mb-2">
                <label class="form-control-label small text-muted font-weight-bold mb-1">Service No *</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'ServiceNo')}"
                  v-model.trim="row.ServiceNo"
                  :disabled="processing || row.status === 'success'"
                  placeholder="e.g. STF/2026/001"
                  @input="validateAllBulkRows"
                />
              </div>

              <!-- Title -->
              <div class="col-6 col-sm-4 mb-2">
                <label class="form-control-label small text-muted font-weight-bold mb-1">Title *</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'Title')}"
                  v-model.trim="row.Title"
                  :disabled="processing || row.status === 'success'"
                  placeholder="Mr / Mrs"
                  @input="validateAllBulkRows"
                />
              </div>

              <!-- Surname -->
              <div class="col-12 col-sm-4 mb-2">
                <label class="form-control-label small text-muted font-weight-bold mb-1">Surname *</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'LastName')}"
                  v-model.trim="row.LastName"
                  :disabled="processing || row.status === 'success'"
                  placeholder="Surname"
                  @input="validateAllBulkRows"
                />
              </div>

              <!-- First Name -->
              <div class="col-6 col-sm-6 mb-2">
                <label class="form-control-label small text-muted font-weight-bold mb-1">First Name *</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'FirstName')}"
                  v-model.trim="row.FirstName"
                  :disabled="processing || row.status === 'success'"
                  placeholder="First name"
                  @input="validateAllBulkRows"
                />
              </div>

              <!-- Other Name -->
              <div class="col-6 col-sm-6 mb-2">
                <label class="form-control-label small text-muted font-weight-bold mb-1">Other Name</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model.trim="row.MiddleName"
                  :disabled="processing || row.status === 'success'"
                  placeholder="Other name"
                />
              </div>

              <!-- Email Address -->
              <div class="col-12 col-sm-6 mb-2">
                <label class="form-control-label small text-muted font-weight-bold mb-1">Email Address *</label>
                <input
                  type="email"
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'EmailAddress')}"
                  v-model.trim="row.EmailAddress"
                  :disabled="processing || row.status === 'success'"
                  placeholder="staff@school.edu.ng"
                  @input="validateAllBulkRows"
                />
              </div>

              <!-- Phone Number -->
              <div class="col-12 col-sm-6 mb-2">
                <label class="form-control-label small text-muted font-weight-bold mb-1">Password *</label>
                <input
                  type="password"
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'UserPassword')}"
                  v-model.trim="row.UserPassword"
                  :disabled="processing || row.status === 'success'"
                  placeholder="Staff account password"
                  autocomplete="new-password"
                  @input="validateAllBulkRows"
                />
              </div>

              <!-- Phone Number -->
              <div class="col-12 col-sm-6 mb-2">
                <label class="form-control-label small text-muted font-weight-bold mb-1">Phone Number</label>
                <input
                  type="tel"
                  class="form-control form-control-sm"
                  v-model.trim="row.PhoneNumber"
                  :disabled="processing || row.status === 'success'"
                  placeholder="08012345678"
                />
              </div>

              <!-- Sex -->
              <div class="col-6 col-sm-4 mb-2">
                <label class="form-control-label small text-muted font-weight-bold mb-1">Sex *</label>
                <select
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'SexId')}"
                  v-model="row.SexId"
                  :disabled="processing || row.status === 'success'"
                  @change="validateAllBulkRows"
                >
                  <option value="0">Select...</option>
                  <option value="10">Male</option>
                  <option value="11">Female</option>
                </select>
              </div>

              <!-- Staff Type -->
              <div class="col-6 col-sm-4 mb-2">
                <label class="form-control-label small text-muted font-weight-bold mb-1">Staff Type *</label>
                <select
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'CategoryId')}"
                  v-model="row.CategoryId"
                  :disabled="processing || row.status === 'success'"
                  @change="validateAllBulkRows"
                >
                  <option value="0">Select...</option>
                  <option value="14">Teaching</option>
                  <option value="15">Non-Teaching</option>
                </select>
              </div>

              <!-- Marital Status -->
              <div class="col-6 col-sm-4 mb-2">
                <label class="form-control-label small text-muted font-weight-bold mb-1">Marital Status</label>
                <select
                  class="form-control form-control-sm"
                  v-model="row.MaritalStatusId"
                  :disabled="processing || row.status === 'success'"
                >
                  <option value="0">Unspecified</option>
                  <option value="12">Married</option>
                  <option value="13">Single</option>
                </select>
              </div>

              <!-- Date of Birth -->
              <div class="col-6 col-sm-6 mb-2">
                <label class="form-control-label small text-muted font-weight-bold mb-1">Date of Birth</label>
                <input
                  type="date"
                  class="form-control form-control-sm"
                  v-model="row.DOB"
                  :disabled="processing || row.status === 'success'"
                  :max="todayDate"
                />
              </div>

              <!-- Full Address -->
              <div class="col-12 col-sm-6 mb-2">
                <label class="form-control-label small text-muted font-weight-bold mb-1">Full Address *</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  :class="{'is-invalid': fieldHasError(row, 'FullAddress')}"
                  v-model.trim="row.FullAddress"
                  :disabled="processing || row.status === 'success'"
                  placeholder="Street, City, State"
                  @input="validateAllBulkRows"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card-footer py-2 bg-light d-flex justify-content-between align-items-center flex-wrap">
        <small class="text-muted mb-2 mb-md-0">
          Showing {{ visibleRows.length }} of {{ rows.length }} records.
          <span v-if="hasValidationErrors" class="text-danger font-weight-bold ml-1 d-block d-sm-inline">
            Resolve {{ totalErrorCount }} error(s) before submit.
          </span>
        </small>
        <div class="d-flex align-items-center flex-wrap">
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary mr-2"
            @click="cancel"
            :disabled="processing"
          >
            Close
          </button>
          <button
            type="button"
            class="btn btn-sm btn-success px-4"
            @click="startBulkSubmission"
            :disabled="processing || pendingOrFailedCount === 0 || hasValidationErrors"
          >
            <i class="fa fa-cloud-upload-alt mr-1"></i>
            {{ processing ? 'Processing...' : `Submit All (${pendingOrFailedCount})` }}
          </button>
        </div>
      </div>
    </div>

    <!-- Empty state when no Excel file imported -->
    <div v-else class="text-center py-5 border rounded bg-white shadow-sm mb-3">
      <i class="fa fa-file-excel fa-4x text-success mb-3"></i>
      <h4 class="text-dark">No Staff Records Loaded Yet</h4>
      <p class="text-muted mb-3" style="max-width: 520px; margin: 0 auto;">
        Upload an Excel file (.xlsx, .xls) or CSV above to extract and preview staff records in the editable table, or start by adding rows manually.
      </p>
      <button
        type="button"
        class="btn btn-sm btn-outline-info mr-2"
        @click="downloadTemplate"
      >
        <i class="fa fa-download mr-1"></i>Download Template
      </button>
      <button
        type="button"
        class="btn btn-sm btn-outline-primary"
        @click="addNewRow"
      >
        <i class="fa fa-plus mr-1"></i>Add Row Manually
      </button>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import moment from "moment";
import Alert from "@/mixins/Alert.mixins";
import PageSpinner from "@/components/PageSpinner";

export default {
  name: "StaffBulkUpload",
  mixins: [Alert],
  components: {
    PageSpinner
  },
  props: {
    staffRepository: {
      type: Object,
      required: true
    },
    existingStaffs: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      onloader: "none",
      selectedFileName: "",
      parseError: "",
      rows: [],
      rowCounter: 0,
      processing: false,
      hasRunOnce: false,
      currentIndex: -1,
      filterErrorsOnly: false,
      viewMode: "auto", // 'auto', 'table', or 'card'
      windowWidth: typeof window !== "undefined" ? window.innerWidth : 1024
    };
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768;
    },
    effectiveViewMode() {
      if (this.viewMode === "auto") {
        return this.isMobile ? "card" : "table";
      }
      return this.viewMode;
    },
    todayDate() {
      return moment().format("YYYY-MM-DD");
    },
    visibleRows() {
      const indexed = this.rows.map((row, idx) => ({ ...row, _originalIndex: idx }));
      if (!this.filterErrorsOnly) return indexed;
      return indexed.filter(
        r => (r._errors && r._errors.length > 0) || r.status === "failed"
      );
    },
    processedCount() {
      return this.rows.filter(r => r.status === "success" || r.status === "failed").length;
    },
    successCount() {
      return this.rows.filter(r => r.status === "success").length;
    },
    failedCount() {
      return this.rows.filter(r => r.status === "failed").length;
    },
    pendingOrFailedCount() {
      return this.rows.filter(r => r.status !== "success").length;
    },
    totalErrorCount() {
      return this.rows.reduce((acc, r) => acc + ((r._errors && r._errors.length) || 0), 0);
    },
    hasValidationErrors() {
      return this.totalErrorCount > 0;
    },
    progressPercentage() {
      if (this.rows.length === 0) return 0;
      return Math.round((this.processedCount / this.rows.length) * 100);
    },
    duplicateWarningCount() {
      return this.rows.filter(
        r => r._errors && r._errors.some(e => e.toLowerCase().includes("duplicate"))
      ).length;
    }
  },
  methods: {
    // ----------------------------------------------------
    // EXCEL FILE UPLOAD & PARSING
    // ----------------------------------------------------
    handleFileUpload(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      this.selectedFileName = file.name;
      this.parseError = "";

      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (!["xlsx", "xls", "csv"].includes(fileExtension)) {
        this.parseError = "Invalid file format. Please upload an Excel (.xlsx, .xls) or CSV file.";
        this.ShowAlertError(this.parseError, "");
        return;
      }

      const reader = new FileReader();
      reader.onload = e => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array", cellDates: true });

          if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
            throw new Error("The uploaded Excel workbook contains no sheets.");
          }

          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const rawRecords = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

          if (!rawRecords || rawRecords.length === 0) {
            this.parseError = "The selected file is empty or contains no data rows.";
            this.ShowAlertWarning("Empty File", this.parseError);
            return;
          }

          this.mapImportedRecords(rawRecords);
          this.ShowAlertSuccess(`Successfully extracted ${rawRecords.length} staff records.`, "");
        } catch (err) {
          this.parseError = err.message || "Failed to parse the Excel file.";
          this.ShowAlertError("File Parse Error", this.parseError);
        }
      };

      reader.onerror = () => {
        this.parseError = "Failed to read file from disk.";
        this.ShowAlertError("Read Error", this.parseError);
      };

      reader.readAsArrayBuffer(file);
    },

    normalizeKey(key) {
      return String(key || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    },

    findValueBySynonyms(obj, synonyms) {
      const normalizedKeys = Object.keys(obj).reduce((acc, k) => {
        acc[this.normalizeKey(k)] = obj[k];
        return acc;
      }, {});

      for (const syn of synonyms) {
        const normSyn = this.normalizeKey(syn);
        if (normalizedKeys[normSyn] !== undefined && normalizedKeys[normSyn] !== "") {
          return normalizedKeys[normSyn];
        }
      }
      return "";
    },

    parseSex(rawVal) {
      if (!rawVal) return 0;
      const s = String(rawVal).trim().toLowerCase();
      if (s === "10" || s === "m" || s === "male" || s === "boy") return 10;
      if (s === "11" || s === "f" || s === "female" || s === "girl") return 11;
      return 0;
    },

    parseMaritalStatus(rawVal) {
      if (!rawVal) return 0;
      const s = String(rawVal).trim().toLowerCase();
      if (s === "12" || s === "married" || s === "m") return 12;
      if (s === "13" || s === "single" || s === "s") return 13;
      return 0;
    },

    parseStaffType(rawVal) {
      if (!rawVal) return 0;
      const s = String(rawVal).trim().toLowerCase();
      if (s === "14" || s === "teaching" || s === "teacher" || s === "academic") return 14;
      if (s === "15" || s === "non-teaching" || s === "non teaching" || s === "nonteaching" || s === "admin" || s === "support") return 15;
      return 0;
    },

    parseDate(rawVal) {
      if (!rawVal) return "";
      if (rawVal instanceof Date && !isNaN(rawVal.getTime())) {
        return moment(rawVal).format("YYYY-MM-DD");
      }
      const str = String(rawVal).trim();
      const parsed = moment(str, ["YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY", "DD-MM-YYYY", "YYYY/MM/DD"], true);
      if (parsed.isValid()) {
        return parsed.format("YYYY-MM-DD");
      }
      const loose = moment(str);
      return loose.isValid() ? loose.format("YYYY-MM-DD") : "";
    },

    parseBool(rawVal) {
      if (!rawVal) return false;
      const s = String(rawVal).trim().toLowerCase();
      return s === "true" || s === "yes" || s === "y" || s === "1";
    },

    mapImportedRecords(records) {
      const mapped = records.map(record => {
        this.rowCounter += 1;

        const serviceNo = String(
          this.findValueBySynonyms(record, [
            "serviceno", "service_no", "staffno", "staff_no", "staffid", "employeeid", "empno", "fileno", "idno"
          ])
        ).trim();

        const title = String(
          this.findValueBySynonyms(record, ["title", "salutation", "prefix", "mrmrs"])
        ).trim();

        const lastName = String(
          this.findValueBySynonyms(record, ["surname", "lastname", "last_name", "sur_name", "familyname"])
        ).trim();

        const firstName = String(
          this.findValueBySynonyms(record, ["firstname", "first_name", "givenname"])
        ).trim();

        const middleName = String(
          this.findValueBySynonyms(record, ["othername", "other_name", "middlename", "middle_name", "othernames"])
        ).trim();

        const email = String(
          this.findValueBySynonyms(record, ["email", "emailaddress", "email_address", "staffemail", "officialemail"])
        ).trim();

        const phone = String(
          this.findValueBySynonyms(record, ["phone", "phoneno", "phonenumber", "phone_number", "telephone", "mobile", "gsm"])
        ).trim();

        const sexRaw = this.findValueBySynonyms(record, ["sex", "gender", "sexid"]);
        const sexId = this.parseSex(sexRaw);

        const typeRaw = this.findValueBySynonyms(record, ["stafftype", "category", "categoryid", "staff_type", "type", "designation"]);
        const categoryId = this.parseStaffType(typeRaw);

        const maritalRaw = this.findValueBySynonyms(record, ["maritalstatus", "marital_status", "status"]);
        const maritalStatusId = this.parseMaritalStatus(maritalRaw);

        const dobRaw = this.findValueBySynonyms(record, ["dob", "dateofbirth", "date_of_birth", "birthdate"]);
        const dob = this.parseDate(dobRaw);

        const fullAddress = String(
          this.findValueBySynonyms(record, ["fulladdress", "address", "homeaddress", "residentialaddress"])
        ).trim();

        const password = String(
          this.findValueBySynonyms(record, ["password", "userpassword", "pwd"])
        ).trim();

        // Operational roles
        const isPrincipal = this.parseBool(this.findValueBySynonyms(record, ["isprincipal", "principal"]));
        const isBursal = this.parseBool(this.findValueBySynonyms(record, ["isbursal", "bursar"]));
        const isPortalAdmin = this.parseBool(this.findValueBySynonyms(record, ["isportaladmin", "portaladmin"]));
        const isHostelMaster = this.parseBool(this.findValueBySynonyms(record, ["ishostelmaster", "hostelmaster"]));
        const isLibraryAdmin = this.parseBool(this.findValueBySynonyms(record, ["islibraryadmin", "libraryadmin"]));
        const isCounselor = this.parseBool(this.findValueBySynonyms(record, ["iscounselor", "counselor"]));
        const isRegistrar = this.parseBool(this.findValueBySynonyms(record, ["isregistrar", "registrar"]));
        const isHod = this.parseBool(this.findValueBySynonyms(record, ["ishod", "hod"]));
        const isDailyAttendanceManager = this.parseBool(this.findValueBySynonyms(record, ["isdailyattendancemanager", "attendancemanager"]));

        return {
          _rowId: `staff-row-${Date.now()}-${this.rowCounter}`,
          ServiceNo: serviceNo,
          Title: title,
          LastName: lastName,
          FirstName: firstName,
          MiddleName: middleName,
          EmailAddress: email,
          PhoneNumber: phone,
          SexId: sexId,
          CategoryId: categoryId,
          MaritalStatusId: maritalStatusId,
          DOB: dob,
          FullAddress: fullAddress,
          UserPassword: password,
          IsPrincipal: isPrincipal,
          IsBursal: isBursal,
          IsPortalAdmin: isPortalAdmin,
          IsHostelMaster: isHostelMaster,
          IsLibraryAdmin: isLibraryAdmin,
          IsCounselor: isCounselor,
          IsRegistrar: isRegistrar,
          IsHOD: isHod,
          IsDailyAttendanceManager: isDailyAttendanceManager,
          status: "pending",
          errorMsg: "",
          _errors: []
        };
      });

      this.rows = mapped;
      this.hasRunOnce = false;
      this.validateAllBulkRows();
    },

    // ----------------------------------------------------
    // VALIDATION
    // ----------------------------------------------------
    validateAllBulkRows() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const seenServiceNos = {};
      const seenEmails = {};

      const existingServiceNos = new Set(
        (this.existingStaffs || [])
          .map(s => String(s.seviceNo || s.serviceNo || s.ServiceNo || "").trim().toLowerCase())
          .filter(Boolean)
      );

      const existingEmails = new Set(
        (this.existingStaffs || [])
          .map(s => String(s.emailAddress || s.EmailAddress || "").trim().toLowerCase())
          .filter(Boolean)
      );

      // Track duplicates within file
      this.rows.forEach(r => {
        const sNo = (r.ServiceNo || "").trim().toLowerCase();
        if (sNo) {
          seenServiceNos[sNo] = (seenServiceNos[sNo] || 0) + 1;
        }
        const email = (r.EmailAddress || "").trim().toLowerCase();
        if (email) {
          seenEmails[email] = (seenEmails[email] || 0) + 1;
        }
      });

      this.rows.forEach(row => {
        const errors = [];
        const sNoKey = (row.ServiceNo || "").trim().toLowerCase();
        const emailKey = (row.EmailAddress || "").trim().toLowerCase();

        if (!row.ServiceNo || !row.ServiceNo.trim()) {
          errors.push("Service number is required");
        } else if (seenServiceNos[sNoKey] > 1) {
          errors.push("Duplicate service number in uploaded file");
        } else if (existingServiceNos.has(sNoKey) && row.status !== "success") {
          errors.push("Service number already exists in staff database");
        }

        if (!row.Title || !row.Title.trim()) {
          errors.push("Title is required");
        }
        if (!row.LastName || !row.LastName.trim()) {
          errors.push("Surname is required");
        }
        if (!row.FirstName || !row.FirstName.trim()) {
          errors.push("First name is required");
        }

        if (!row.EmailAddress || !row.EmailAddress.trim()) {
          errors.push("Email address is required");
        } else if (!emailRegex.test(row.EmailAddress.trim())) {
          errors.push("Invalid email address format");
        } else if (seenEmails[emailKey] > 1) {
          errors.push("Duplicate email address in uploaded file");
        } else if (existingEmails.has(emailKey) && row.status !== "success") {
          errors.push("Email address already exists in staff database");
        }

        if (!row.UserPassword || !row.UserPassword.trim()) {
          errors.push("Password is required");
        }

        if (!row.SexId || row.SexId === 0 || row.SexId === "0") {
          errors.push("Sex selection is required");
        }

        if (!row.CategoryId || row.CategoryId === 0 || row.CategoryId === "0") {
          errors.push("Staff type (Teaching/Non-Teaching) is required");
        }

        if (!row.FullAddress || !row.FullAddress.trim()) {
          errors.push("Full address is required");
        }

        row._errors = errors;
      });
    },

    fieldHasError(row, fieldName) {
      if (!row._errors || row._errors.length === 0) return false;
      const lowerErrors = row._errors.map(e => e.toLowerCase());
      if (fieldName === "ServiceNo") return lowerErrors.some(e => e.includes("service"));
      if (fieldName === "Title") return lowerErrors.some(e => e.includes("title"));
      if (fieldName === "LastName") return lowerErrors.some(e => e.includes("surname"));
      if (fieldName === "FirstName") return lowerErrors.some(e => e.includes("first name"));
      if (fieldName === "EmailAddress") return lowerErrors.some(e => e.includes("email"));
      if (fieldName === "UserPassword") return lowerErrors.some(e => e.includes("password"));
      if (fieldName === "SexId") return lowerErrors.some(e => e.includes("sex"));
      if (fieldName === "CategoryId") return lowerErrors.some(e => e.includes("staff type"));
      if (fieldName === "FullAddress") return lowerErrors.some(e => e.includes("address"));
      return false;
    },

    // ----------------------------------------------------
    // TABLE ACTIONS
    // ----------------------------------------------------
    addNewRow() {
      this.rowCounter += 1;
      const baseYear = new Date().getFullYear();
      this.rows.unshift({
        _rowId: `staff-row-${Date.now()}-${this.rowCounter}`,
        ServiceNo: `STF/${baseYear}/${String(this.rows.length + 1).padStart(3, "0")}`,
        Title: "Mr",
        LastName: "",
        FirstName: "",
        MiddleName: "",
        EmailAddress: "",
        PhoneNumber: "",
        SexId: 10,
        CategoryId: 14,
        MaritalStatusId: 13,
        DOB: "",
        FullAddress: "",
        UserPassword: "",
        IsPrincipal: false,
        IsBursal: false,
        IsPortalAdmin: false,
        IsHostelMaster: false,
        IsLibraryAdmin: false,
        IsCounselor: false,
        IsRegistrar: false,
        IsHOD: false,
        IsDailyAttendanceManager: false,
        status: "pending",
        errorMsg: "",
        _errors: []
      });
      this.validateAllBulkRows();
    },

    autofillMissingServiceNos() {
      const year = new Date().getFullYear();
      let assignedCount = 0;
      this.rows.forEach((r, idx) => {
        if (!r.ServiceNo || !r.ServiceNo.trim()) {
          r.ServiceNo = `STF/${year}/${String(idx + 1).padStart(3, "0")}`;
          assignedCount += 1;
        }
      });
      this.validateAllBulkRows();
      if (assignedCount > 0) {
        this.ShowAlertSuccess(`Assigned service numbers to ${assignedCount} row(s).`, "");
      } else {
        this.ShowAlertWarning("All rows already have service numbers.", "");
      }
    },

    removeRow(index) {
      if (index >= 0 && index < this.rows.length) {
        this.rows.splice(index, 1);
        this.validateAllBulkRows();
      }
    },

    resetBulkUpload() {
      this.rows = [];
      this.selectedFileName = "";
      this.parseError = "";
      this.processing = false;
      this.hasRunOnce = false;
      this.currentIndex = -1;
      if (this.$refs.staffUploadComp && this.$refs.staffUploadComp.clear) {
        this.$refs.staffUploadComp.clear();
      } else if (this.$refs.staffExcelFileInput) {
        this.$refs.staffExcelFileInput.value = "";
      }
    },

    // ----------------------------------------------------
    // SEQUENTIAL BACKGROUND SUBMISSION
    // ----------------------------------------------------
    async startBulkSubmission() {
      this.validateAllBulkRows();
      if (this.hasValidationErrors) {
        this.ShowAlertWarning(
          "Validation Errors Found",
          `Please fix the ${this.totalErrorCount} error(s) highlighted in red before submitting.`
        );
        return;
      }

      const pendingRows = this.rows.filter(r => r.status !== "success");
      if (pendingRows.length === 0) {
        this.ShowAlertSuccess("All staff records have already been uploaded successfully.", "");
        return;
      }

      this.processing = true;
      this.hasRunOnce = true;

      for (let i = 0; i < this.rows.length; i++) {
        const row = this.rows[i];
        if (row.status === "success") continue;

        this.currentIndex = i;
        row.status = "processing";
        row.errorMsg = "";

        const payload = {
          Id: 0,
          ServiceNo: row.ServiceNo,
          Title: row.Title,
          FirstName: row.FirstName,
          MiddleName: row.MiddleName || "",
          LastName: row.LastName,
          PhoneNumber: row.PhoneNumber || "",
          EmailAddress: row.EmailAddress,
          Passport: "",
          SexId: Number(row.SexId),
          MaritalStatusId: Number(row.MaritalStatusId || 0),
          CategoryId: Number(row.CategoryId),
          IsPrincipal: Boolean(row.IsPrincipal),
          IsBursal: Boolean(row.IsBursal),
          IsPortalAdmin: Boolean(row.IsPortalAdmin),
          IsHostelMaster: Boolean(row.IsHostelMaster),
          IsLibraryAdmin: Boolean(row.IsLibraryAdmin),
          IsCounselor: Boolean(row.IsCounselor),
          IsRegistrar: Boolean(row.IsRegistrar),
          IsHOD: Boolean(row.IsHOD),
          IsDailyAttendanceManager: Boolean(row.IsDailyAttendanceManager),
          FullAddress: row.FullAddress,
          DOB: row.DOB || "",
          UserPassword: row.UserPassword
        };

        try {
          const saved = await this.staffRepository.create(payload);
          row.status = "success";
          row.errorMsg = "";
          row._errors = [];
          this.$emit("staff-created", saved);
        } catch (err) {
          row.status = "failed";
          row.errorMsg = (err && (err.responseMessage || err.message)) || "Submission failed";
        }

        // Small yield to allow DOM updates, progress bar animation, and smooth UI
        await new Promise(resolve => setTimeout(resolve, 80));
      }

      this.processing = false;
      this.currentIndex = -1;

      if (this.failedCount === 0) {
        this.ShowAlertSuccess(
          "Staff Import Complete",
          `All ${this.successCount} staff records were successfully submitted and queued for background synchronization.`
        );
      } else {
        this.ShowAlertWarning(
          "Staff Import Finished with Errors",
          `${this.successCount} record(s) uploaded successfully. ${this.failedCount} record(s) failed. You can review and retry failed records.`
        );
      }
    },

    retryFailed() {
      this.rows.forEach(r => {
        if (r.status === "failed") {
          r.status = "pending";
          r.errorMsg = "";
        }
      });
      this.startBulkSubmission();
    },

    downloadTemplate() {
      const templateData = [
        {
          "Service Number": "STF/2026/001",
          "Title": "Mr",
          "Surname": "Adeyemi",
          "First Name": "Samuel",
          "Other Name": "Oluwaseun",
          "Email Address": "s.adeyemi@school.edu.ng",
          "Phone Number": "08031234567",
          "Sex": "Male",
          "Staff Type": "Teaching",
          "Marital Status": "Married",
          "Date of Birth": "1988-04-12",
          "Full Address": "25 Academic Lane, Ikeja, Lagos",
          "Password": "SchoolDock@123",
          "Is Principal": "No",
          "Is Bursar": "No",
          "Is Portal Admin": "No"
        },
        {
          "Service Number": "STF/2026/002",
          "Title": "Mrs",
          "Surname": "Okeke",
          "First Name": "Grace",
          "Other Name": "Ngozi",
          "Email Address": "g.okeke@school.edu.ng",
          "Phone Number": "08059876543",
          "Sex": "Female",
          "Staff Type": "Teaching",
          "Marital Status": "Married",
          "Date of Birth": "1992-09-25",
          "Full Address": "14 Victoria Island Road, Lagos",
          "Password": "SchoolDock@123",
          "Is Principal": "No",
          "Is Bursar": "No",
          "Is Portal Admin": "No"
        },
        {
          "Service Number": "STF/2026/003",
          "Title": "Mr",
          "Surname": "Babatunde",
          "First Name": "Ibrahim",
          "Other Name": "Ali",
          "Email Address": "i.babatunde@school.edu.ng",
          "Phone Number": "08071122334",
          "Sex": "Male",
          "Staff Type": "Non-Teaching",
          "Marital Status": "Single",
          "Date of Birth": "1995-11-03",
          "Full Address": "8 Airport Crescent, Ikeja, Lagos",
          "Password": "SchoolDock@123",
          "Is Principal": "No",
          "Is Bursar": "Yes",
          "Is Portal Admin": "No"
        }
      ];

      const ws = XLSX.utils.json_to_sheet(templateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Staff_Template");
      XLSX.writeFile(wb, "Staff_Bulk_Upload_Template.xlsx");
    },

    onResize() {
      if (typeof window !== "undefined") {
        this.windowWidth = window.innerWidth;
      }
    },

    cancel() {
      this.$emit("close");
    }
  },
  mounted() {
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.onResize);
    }
  },
  beforeDestroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.onResize);
    }
  }
};
</script>

<style scoped>
.staff-bulk-upload {
  position: relative;
}

.bulk-table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.bulk-table {
  font-size: 13px;
  min-width: 1400px; /* guarantees full columns can be smoothly scrolled on narrow viewports */
}

.bulk-table thead th {
  background-color: #f8fafc;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e2e8f0;
  padding: 0.55rem 0.5rem;
}

.bulk-table td {
  padding: 0.4rem 0.5rem;
}

.table-danger-light {
  background-color: rgba(254, 226, 226, 0.45) !important;
}

.table-success-light {
  background-color: rgba(220, 252, 231, 0.45) !important;
}

.table-info-light {
  background-color: rgba(224, 242, 254, 0.55) !important;
}

.custom-file-label::after {
  content: "Browse";
  background-color: #5e72e4;
  color: #fff;
}

/* Mobile card list styling */
.mobile-cards-container {
  background-color: #f8fafc;
  border-radius: 6px;
}

.mobile-record-card {
  border-radius: 8px;
  background: #ffffff;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.mobile-record-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.mobile-record-card .form-control-label {
  font-size: 11px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

@media (max-width: 576px) {
  .bulk-table-wrapper {
    max-height: 400px !important;
  }
}
</style>
